import React, { memo, FC } from "react";
import Typography from "@mui/material/Typography";
import Choices from "../Choices";
import Paper from "@mui/material/Paper";
import ReusableAccordian from "../ReusableAccordion";
import { Image } from "cloudinary-react";
import PleaseNote from "../PleaseNote";
import {
  woodExtStops,
  woodIntStops,
  stops,
  woodInteriorStopsImages,
  stopsImages,
  stopsAlum,
  alumStopsImages,
  stopsAlumSquareProfile,
  alumStopsImagesSquareProfile,
} from "./defaults";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useStopsSelector from "./useStopsSelector";

const ColLayoutSpace = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1, 2),
}));

const borderColor = "#04bff3";

function StickStopsAccordian() {
  const {
    handleClickExt,
    handleClickInt,
    handleClickStop,
    material,
    stopsInterior,
    stopsExterior,
    stickingStops,
    stilesAndRails,
    isAlum,
    stilesRailsProfile,
    isPicoBifold,
    cladStopsOptions
  } = useStopsSelector();

  return (
    <>
      {material && (
        <ReusableAccordian
          accordionName="stickStops"
          heading="Sticking/Stops"
          secondaryHeading="Select sticking/stops shape"
          colOrRow="col"
        >
          {isAlum && stilesRailsProfile !== "Square" && (
            <PleaseNote notes={`Overall glass thickness will be 1-1/4" with Bevel stops`} />
          )}

          {isPicoBifold && (
            <PleaseNote notes="The integrated door pull will have a cleaner, more concealed appearance with the square stop compared to the bevel." />
          )}

          {material === "All Wood" ? (
            <>
              <RowLayout>
                <ColLayoutSpace>
                  <ColLayout>
                    <Typography variant="subtitle1">
                      * choose an interior sticking
                    </Typography>
                    <RowLayout>
                      {woodIntStops.map((state) => (
                        <div
                          key={state.id}
                          onClick={() => handleClickInt(state.label)}
                        >
                          <Choices
                            label={state.label}
                            variant={
                              state.label === stopsInterior
                                ? "filled"
                                : "outlined"
                            }
                            checkMark={state.label === stopsInterior}
                          />
                        </div>
                      ))}
                    </RowLayout>
                  </ColLayout>
                  <RowLayout style={{ marginLeft: 50 }}>
                    <StopsImages
                      stickingStops={stopsInterior || ""}
                      handleClick={handleClickInt}
                      isInterior={true}
                    />
                  </RowLayout>
                </ColLayoutSpace>

                <ColLayoutSpace>
                  <ColLayout>
                    <Typography variant="subtitle1">
                      * choose an exterior stop
                    </Typography>
                    <RowLayout>
                      {woodExtStops.map((state) => (
                        <div
                          key={state.id}
                          onClick={() => handleClickExt(state.label)}
                        >
                          <Choices
                            label={state.label}
                            variant={
                              state.label === stopsExterior
                                ? "filled"
                                : "outlined"
                            }
                            checkMark={state.label === stopsExterior}
                          />
                        </div>
                      ))}
                    </RowLayout>
                  </ColLayout>
                  <RowLayout style={{ marginLeft: 50 }}>
                    <StopsImages
                      stickingStops={stopsExterior || ""}
                      handleClick={handleClickExt}
                    />
                  </RowLayout>
                </ColLayoutSpace>
              </RowLayout>
            </>
          ) : isAlum && stilesRailsProfile === "Square" ? (
            <ColLayout>
              <PleaseNote notes="Square Stops only option for Square Stiles Profile" />
              <RowLayout>
                <div>
                  <Choices
                    label={stopsAlumSquareProfile.label}
                    variant={
                      stopsAlumSquareProfile.label === stickingStops
                        ? "filled"
                        : "outlined"
                    }
                    checkMark={stopsAlumSquareProfile.label === stickingStops}
                    disabled={true}
                    wide={true}
                  />
                </div>
              </RowLayout>
              <RowLayout>
                <StopsImages
                  isAlum={true}
                  stickingStops={stickingStops || ""}
                  handleClick={handleClickStop}
                  isInterior={false}
                  stilesRailsProfile={stilesRailsProfile}
                />
              </RowLayout>
            </ColLayout>
          ) : isAlum ? (
            <>
              <RowLayout>
                {stopsAlum.map((state) => (
                  <div
                    key={state.id}
                    onClick={() => handleClickStop(state.label)}
                  >
                    <Choices
                      label={state.label}
                      variant={
                        state.label === stickingStops ? "filled" : "outlined"
                      }
                      checkMark={state.label === stickingStops}
                      wide={true}
                    />
                  </div>
                ))}
              </RowLayout>
            </>
          ) : (
            <>
              <RowLayout>
                {cladStopsOptions.map((state) => (
                  <div
                    key={state.id}
                    onClick={() => handleClickStop(state.label)}
                  >
                    <Choices
                      label={state.label}
                      variant={
                        state.label === stickingStops ? "filled" : "outlined"
                      }
                      checkMark={state.label === stickingStops}
                    />
                  </div>
                ))}
              </RowLayout>
              <RowLayout style={{ marginLeft: 50 }}>
                <StopsImages
                  stickingStops={stickingStops || ""}
                  handleClick={handleClickStop}
                  stiles={stilesAndRails}
                />
              </RowLayout>
            </>
          )}
        </ReusableAccordian>
      )}
    </>
  );
}

interface StopsImagesProps {
  stickingStops: string;
  handleClick: (label: string) => void;
  isAlum?: boolean;
  stilesRailsProfile?: string;
  isInterior?: boolean;
  stiles?: string;
}

const StopsImages: FC<StopsImagesProps> = memo(
  ({
    stickingStops,
    handleClick,
    isInterior,
    stiles,
    isAlum,
    stilesRailsProfile,
  }) => {
    const images =
      isAlum && stilesRailsProfile !== "Square"
        ? alumStopsImages
        : isAlum
          ? alumStopsImagesSquareProfile
          : !isInterior && stiles?.includes("Pico")
            ? stopsImages.filter((itm) => itm.label !== "Ovolo")
            : !isInterior
              ? stopsImages
              : woodInteriorStopsImages;

    const publicId = (label: string) =>
      isAlum
        ? `Stops/alum_${label.toLowerCase()}`
        : `Stops/${label.toLowerCase()}`;

    return (
      <>
        {images.map((image) => (
          <Paper
            key={image.id}
            style={{
              border:
                image.label === stickingStops ? `2px solid ${borderColor}` : "",
              marginLeft: 10,
              marginRight: 10,
            }}
            elevation={3}
          >
            <Image
              onClick={() => handleClick(image.label)}
              cloudName="ag-millworks"
              secure="true"
              publicId={publicId(image.label)}
            />
          </Paper>
        ))}
      </>
    );
  },
);

export default memo(StickStopsAccordian);
